/**
 * DAC profiles -- group 04 (British and North American network/streaming DACs).
 *
 * This group is deliberately weighted toward integrated streamer-DACs and
 * digital-preamplifier products, because that is where the interesting
 * architectural divergence now sits: an off-the-shelf delta-sigma part fed by a
 * conventional oversampling filter behaves very differently from a paralleled
 * sign-magnitude ladder, from an in-house FPGA converter, and from a design
 * whose defining choice is the reconstruction filter rather than the converter.
 *
 * Confidence tagging follows the project rule. Where a manufacturer publishes
 * distortion and dynamic-range numbers the entry is MANUFACTURER_SPEC; where the
 * brand publishes architecture but not measured electrical performance (which is
 * the norm for Naim, Linn and Meridian) the entry is ESTIMATED and the
 * provenanceNote says exactly which numbers were inferred. Nothing is MEASURED.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  makeDac,
  usd,
} from '../kit';

export const DACS_04: DacProfile[] = [
  makeDac({
    mfr: 'Lumin',
    model: 'X1',
    price: usd(13990, 'APPROXIMATE', 2023, 'Approximate US retail. Lumin pricing is set regionally and the figure quoted here is recalled rather than read from a current price list.'),
    year: 2018,
    country: 'Hong Kong',
    architecture: 'delta-sigma',
    core: 'Dual ESS ES9038PRO, one part per channel with all eight internal converter channels summed',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 128,
    thdPlusNDb: -114,
    crosstalkDb: -126,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 10,
    balanced: true,
    dsd: 'dsd512',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: ESS_FILTERS(),
    defaultFilterId: 'sharp-linear',
    parallelUnits: 8,
    noiseShaperOrder: 7,
    jitterPsRms: 22,
    confidence: 'ESTIMATED',
    accent: '#7fd0ff',
    tags: ['streamer-dac', 'ess', 'dual-mono', 'network', 'optical-ethernet'],
    notes:
      'Two ES9038PRO parts run one per channel, so all eight internal converter channels are paralleled into a single output rather than split across stereo. Paralleling averages the independent modulator noise of each channel, which is why the noise floor sits several dB below what a single shared part achieves at the same THD. The consequence in this model is a device with a very low broadband noise floor but a distortion residue that is still delta-sigma in structure: low order, level-independent, and dominated by the analog output stage rather than by the converter.',
    provenanceNote:
      'Converter part, dual-mono arrangement and rate support are from Lumin published material. Dynamic range, THD+N and channel separation are ESTIMATED from ES9038PRO datasheet performance in the described configuration -- Lumin does not publish measured electrical specifications for the X1. Output impedance and the assumption of a Class-A-biased discrete output stage are inferred from the published circuit description.',
  }),

  makeDac({
    mfr: 'Auralic',
    model: 'VEGA G2.1',
    price: usd(7999, 'APPROXIMATE', 2021, 'Approximate US retail near launch; recalled figure, not read from a current dealer price list.'),
    year: 2019,
    country: 'China',
    architecture: 'hybrid',
    core: 'Tesla G2 FPGA processing platform performing all upsampling and filtering ahead of an ESS ES9038PRO converter',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 130,
    thdPlusNDb: -118,
    crosstalkDb: -130,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 5,
    balanced: true,
    dsd: 'dsd512',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [
      { ...FILTERS.sharpLinear(256), id: 'precise', label: 'Precise (long linear phase)', stopbandDb: -130, cutoffRatio: 0.92, transitionRatio: 0.07 },
      { ...FILTERS.apodizing(160), id: 'dynamic', label: 'Dynamic (apodizing)' },
      { ...FILTERS.hybrid(96), id: 'balance', label: 'Balance (hybrid phase)' },
      { ...FILTERS.slowMinimum(32), id: 'smooth', label: 'Smooth (slow minimum phase)' },
    ],
    defaultFilterId: 'precise',
    internalOversampling: 32,
    noiseShaperOrder: 6,
    jitterPsRms: 10,
    effectiveBits: 21,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9a6ff',
    tags: ['fpga-front-end', 'ess', 'femto-clock', 'selectable-filter', 'streamer-dac'],
    notes:
      'The distinguishing element is not the converter but the FPGA in front of it: all interpolation is performed in the processing platform and the ES9038PRO is run with its own filters bypassed, so the four selectable modes are genuinely different impulse responses rather than datasheet presets. The dual femtosecond clocks make jitter a negligible term compared with the filter choice, so this profile deliberately places most of its audible variation in the reconstruction filter and almost none in the timing model. Distortion structure remains delta-sigma-like and level-independent.',
    provenanceNote:
      'Dynamic range (130 dB A-weighted) and THD+N (0.00012 percent, restated here as -118 dB) are Auralic published specifications, hence MANUFACTURER_SPEC. Output levels are given as the nominal 2 V / 4 V values and are not confirmed against the datasheet. Jitter, internal oversampling ratio and the shape of each of the four named filters are ESTIMATED from the published description of the platform.',
  }),

  makeDac({
    mfr: 'Naim Audio',
    model: 'ND 555',
    price: usd(19000, 'APPROXIMATE', 2023, 'Approximate US retail for the ND 555 head unit alone; the required external 555 PS DR power supply is a separate purchase of comparable cost.'),
    year: 2018,
    country: 'United Kingdom',
    architecture: 'ladder-multibit',
    core: 'Paired Burr-Brown PCM1704K sign-magnitude ladder converters with a Naim 40-bit SHARC DSP oversampling filter',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 117,
    thdPlusNDb: -102,
    crosstalkDb: -98,
    outputVrmsSE: 2.1,
    outputImpedanceOhms: 22,
    balanced: false,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 24,
    filters: [
      { ...FILTERS.sharpLinear(512), id: 'sharc-16fs', label: 'Naim 40-bit SHARC oversampling filter', stopbandDb: -132, cutoffRatio: 0.91, transitionRatio: 0.08 },
      FILTERS.nosCompensated(),
    ],
    defaultFilterId: 'sharc-16fs',
    ladderMismatchLsb: 0.35,
    effectiveBits: 20,
    internalOversampling: 16,
    jitterPsRms: 28,
    harmonicShape: HARMONIC_SHAPES.r2r,
    confidence: 'ESTIMATED',
    accent: '#5fd08a',
    tags: ['ladder', 'pcm1704', 'sharc-filter', 'single-ended', 'din-output'],
    notes:
      'A sign-magnitude ladder splits positive and negative halves of the waveform across separate resistor arrays, so its dominant error is a discontinuity at the zero crossing rather than the broadband modulator noise of a delta-sigma part. This profile therefore models level-dependent INL that does not shrink as signal level falls, which is the opposite behaviour to every ESS and AKM design in this group. Outputs are single-ended only, and the relatively high source impedance means the following preamplifier input impedance is a real term in the frequency response rather than a formality.',
    provenanceNote:
      'Converter family, the 40-bit SHARC filter and the single-ended DIN/RCA output arrangement are from Naim published material. Dynamic range, THD+N, channel separation, output impedance and ladder mismatch are ESTIMATED from PCM1704K datasheet grades and from norms for discrete ladder implementations -- Naim does not publish distortion or dynamic-range figures for the ND 555. The non-oversampling option in the filter list is offered as a modelling alternative for comparison and is not a selectable mode on the real product.',
  }),

  makeDac({
    mfr: 'Linn',
    model: 'Klimax DSM',
    price: usd(39000, 'APPROXIMATE', 2023, 'Approximate US retail for the Organik-equipped Klimax DSM; Linn prices vary substantially by market and finish, so this is a recalled figure.'),
    year: 2021,
    country: 'United Kingdom',
    architecture: 'fpga-custom',
    core: 'Linn Organik: in-house FPGA-controlled converter with discrete reference and locally regulated analog section',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 124,
    thdPlusNDb: -110,
    crosstalkDb: -118,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 20,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [
      { ...FILTERS.brickwall(1536), id: 'organik-fixed', label: 'Organik upsampling (fixed, no user choice)', stopbandDb: -150, cutoffRatio: 0.95, transitionRatio: 0.04 },
    ],
    defaultFilterId: 'organik-fixed',
    internalOversampling: 128,
    effectiveBits: 21,
    jitterPsRms: 9,
    confidence: 'ESTIMATED',
    accent: '#e0e0e0',
    tags: ['in-house-converter', 'fpga', 'fixed-filter', 'streamer-dac', 'no-user-filter'],
    notes:
      'Linn replaced the bought-in converter chip with its own FPGA-driven design, and the important behavioural consequence is that there is exactly one reconstruction filter with no user choice -- so unlike the Auralic and Cambridge entries here, filter selection contributes nothing to this profile and all of its character comes from the converter and analog stage. The filter modelled is very long and close to brick-wall, which trades symmetric pre- and post-ringing on band-edge transients against essentially no in-band ripple. Clock jitter is set low because the converter clock and the reference sit on the same locally regulated island.',
    provenanceNote:
      'The Organik architecture, its in-house FPGA control and the absence of user-selectable filtering are from Linn published material. Dynamic range, THD+N, channel separation, output level, output impedance and jitter are all ESTIMATED -- Linn publishes no electrical performance specification for this product. The filter length and stopband figure are modelled, not quoted.',
  }),

  makeDac({
    mfr: 'Meridian Audio',
    model: 'Ultra DAC',
    price: usd(23000, 'APPROXIMATE', 2023, 'Approximate US retail; recalled figure rather than a current quotation.'),
    year: 2017,
    country: 'United Kingdom',
    architecture: 'multibit-delta-sigma',
    core: 'Eight paralleled current-output converter channels per output, fed by Meridian apodizing upsampling',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -106,
    crosstalkDb: -112,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 47,
    balanced: true,
    dsd: 'dsd64',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [
      { ...FILTERS.apodizing(320), id: 'meridian-apodizing', label: 'Meridian apodizing (default)', stopbandDb: -112, cutoffRatio: 0.83, transitionRatio: 0.19 },
      { ...FILTERS.sharpLinear(128), id: 'meridian-linear', label: 'Linear-phase reference' },
    ],
    defaultFilterId: 'meridian-apodizing',
    parallelUnits: 8,
    noiseShaperOrder: 5,
    jitterPsRms: 35,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#d0b0ff',
    tags: ['apodizing', 'parallel-converters', 'upsampling', 'filter-led-design'],
    notes:
      'This is the one design in the group whose defining engineering choice is the filter rather than the converter: the apodizing response deliberately truncates before Nyquist and accepts a wider transition band in exchange for suppressing the pre-ringing contributed by whatever anti-alias filter was used at the recording stage. That trade shows up in this profile as a low pre-ring term with an early roll-off and a comparatively modest stopband figure -- numerically worse than a brick-wall filter, intentionally. Paralleling eight converter channels per output lowers the noise floor by averaging uncorrelated modulator noise while leaving the distortion structure unchanged.',
    provenanceNote:
      'The apodizing filter concept, the paralleled-converter arrangement and the upsampling topology are from Meridian published material and from the published Craven/Stuart work on apodizing reconstruction. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED -- Meridian does not publish a full electrical specification for the Ultra DAC. Filter cutoff and stopband values are modelled to reproduce the described behaviour, not quoted.',
  }),

  makeDac({
    mfr: 'Cambridge Audio',
    model: 'Azur 851D',
    price: usd(1499, 'APPROXIMATE', 2018, 'Approximate US retail while current; the model is discontinued and this is a recalled launch-era figure.'),
    year: 2015,
    country: 'United Kingdom',
    architecture: 'multibit-delta-sigma',
    core: 'Dual Analog Devices AD1955 multi-bit sigma-delta in dual-differential, fed by Anagram ATF2 upsampling to 24-bit/384 kHz',
    outputStage: 'op-amp',
    dynamicRangeDb: 118,
    thdPlusNDb: -103,
    crosstalkDb: -105,
    outputVrmsSE: 2.1,
    outputVrmsBal: 4.2,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [
      { ...FILTERS.sharpLinear(160), id: 'atf2-linear', label: 'ATF2 upsampling, linear phase' },
      { ...FILTERS.sharpMinimum(112), id: 'atf2-minimum', label: 'ATF2 upsampling, minimum phase' },
      { ...FILTERS.brickwall(640), id: 'atf2-steep', label: 'ATF2 upsampling, steep', stopbandDb: -140, cutoffRatio: 0.94, transitionRatio: 0.045 },
    ],
    defaultFilterId: 'atf2-linear',
    parallelUnits: 2,
    noiseShaperOrder: 5,
    internalOversampling: 8,
    jitterPsRms: 55,
    effectiveBits: 19,
    confidence: 'ESTIMATED',
    accent: '#8fa3b8',
    tags: ['ad1955', 'atf2-upsampling', 'dual-differential', 'digital-preamp'],
    notes:
      'The AD1955 is a multi-bit sigma-delta rather than a single-bit modulator, so its idle-tone behaviour is much better behaved than a first-generation one-bit part but its noise floor is higher than a modern ESS device by roughly ten dB. Running two parts dual-differential cancels even-order errors in the converter itself, which pushes the residual distortion signature toward the op-amp output stage and toward odd order. Of the three selectable filters, the steep option is the only one that is genuinely close to brick-wall; the other two differ mainly in phase, not in stopband.',
    provenanceNote:
      'Converter part, dual-differential arrangement, ATF2 upsampling and the three named filter options are from Cambridge Audio published material. The dynamic range and THD+N values here are ESTIMATED from AD1955 datasheet performance in this configuration and are not transcribed from a Cambridge specification sheet. Output impedance, jitter and per-filter tap counts are modelled.',
  }),

  makeDac({
    mfr: 'Simaudio Moon',
    model: '780D v2',
    price: usd(16000, 'APPROXIMATE', 2022, 'Approximate US retail; recalled figure rather than a current quotation.'),
    year: 2018,
    country: 'Canada',
    architecture: 'delta-sigma',
    core: 'ESS SABRE32 delta-sigma converter with MOON M-AJiC32 asynchronous clock-domain jitter control',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 122,
    thdPlusNDb: -112,
    crosstalkDb: -120,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [
      { ...FILTERS.sharpLinear(128), id: 'moon-fixed', label: 'Fixed linear phase (no user selection)' },
    ],
    defaultFilterId: 'moon-fixed',
    noiseShaperOrder: 6,
    jitterPsRms: 14,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#c8a6ff',
    tags: ['ess', 'reclocking', 'fixed-filter', 'digital-preamp', 'modular'],
    notes:
      'The design premise is that the incoming clock is discarded entirely: data is buffered and re-emitted on a locally generated clock, so source-side jitter is decoupled rather than merely attenuated by a PLL. That is modelled here as a low, purely random jitter term with no periodic component, which is the audible difference from a PLL-locked design whose residual jitter tracks the interface rate. Like the Linn entry there is no user filter choice, so the profile has a single fixed reconstruction response and derives its character from the discrete output stage.',
    provenanceNote:
      'The asynchronous reclocking scheme (M-AJiC32) and the use of an ESS SABRE32 core are from Simaudio published material; the exact converter part number is not confirmed here and the core string is deliberately generic. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED from norms for this class of ESS implementation. The single fixed filter reflects the absence of a published user filter menu.',
  }),

  makeDac({
    mfr: 'Bryston',
    model: 'BDA-3',
    price: usd(3495, 'APPROXIMATE', 2020, 'Approximate US retail while current; recalled figure, and Bryston pricing was revised more than once over the run.'),
    year: 2016,
    country: 'Canada',
    architecture: 'delta-sigma',
    core: 'Four AKM AK4490 converters arranged as a dual-differential pair per channel',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 123,
    thdPlusNDb: -108,
    crosstalkDb: -115,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: AKM_FILTERS(),
    defaultFilterId: 'sharp-linear',
    parallelUnits: 4,
    noiseShaperOrder: 5,
    jitterPsRms: 40,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#9fb0c0',
    tags: ['akm', 'dual-differential', 'discrete-output', 'no-op-amps'],
    notes:
      'Bryston builds the analog stage from discrete transistors rather than integrated op-amps, and that is where this profile differs most from the Cambridge entry that shares a similar converter class: the residual harmonic series decays faster and stays second-order dominant instead of flattening into higher odd orders. The AK4490 filter menu includes a super-slow roll-off option whose stopband is only about thirty dB, which is audibly different from the other choices rather than a phase-only variation. Output impedance is comparatively high for a solid-state design and will interact with short-cable capacitance and low-impedance preamp inputs.',
    provenanceNote:
      'Converter part, the four-chip dual-differential arrangement and the discrete analog output stage are from Bryston published material. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED from AK4490 datasheet performance in this configuration; the figures here are not transcribed from a Bryston specification sheet. The filter menu follows the AK4490 datasheet roll-off options.',
  }),
];
